import { useRef, useState } from "react"
import {Upload, X, FileText, Loader2, AlertCircle, CheckCircle2} from 'lucide-react'
import { lumi } from "@/lib/lumi"

export interface AppealAttachment {
  id: string
  fileName: string
  fileUrl?: string
  previewUrl?: string
  uploading: boolean
  error?: string
  isImage: boolean
}

interface AppealFileUploadProps {
  attachments: AppealAttachment[]
  setAttachments: React.Dispatch<React.SetStateAction<AppealAttachment[]>>
}

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "application/pdf"]
const MAX_FILES = 10

function AppealFileUpload({ attachments, setAttachments }: AppealFileUploadProps) {
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const processFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return

    const incoming = Array.from(fileList).filter((file) => ACCEPTED_TYPES.includes(file.type))
    const availableSlots = Math.max(0, MAX_FILES - attachments.length)
    const filesToUpload = incoming.slice(0, availableSlots)
    if (filesToUpload.length === 0) return

    const pendingItems: AppealAttachment[] = filesToUpload.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fileName: file.name,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      uploading: true,
      isImage: file.type.startsWith("image/"),
    }))

    setAttachments((prev) => [...prev, ...pendingItems])

    try {
      const results = await lumi.tools.file.upload(filesToUpload)
      setAttachments((prev) =>
        prev.map((item) => {
          const index = pendingItems.findIndex((p) => p.id === item.id)
          if (index === -1) return item
          const result = results[index]
          return {
            ...item,
            uploading: false,
            fileUrl: result?.fileUrl,
            error: result?.uploadError,
          }
        })
      )
    } catch (error) {
      console.error("Supporting document upload failed:", error)
      setAttachments((prev) =>
        prev.map((item) =>
          pendingItems.some((p) => p.id === item.id)
            ? { ...item, uploading: false, error: "Upload failed. Please try again." }
            : item
        )
      )
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    processFiles(e.dataTransfer.files)
  }

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id))
  }

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragActive(true)
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all ${
          dragActive
            ? "border-sky-500 bg-sky-50"
            : "border-slate-300 bg-slate-50 hover:border-sky-400 hover:bg-sky-50/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.pdf"
          className="hidden"
          onChange={(e) => processFiles(e.target.files)}
        />
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-sky-100 text-sky-600">
          <Upload className="h-6 w-6" />
        </div>
        <p className="font-semibold text-slate-800">Drop files here or click to upload</p>
        <p className="mt-1 text-sm text-slate-500">
          Photos, receipts, payment confirmations, permits, or visitor registration confirmations
        </p>
        <p className="mt-2 text-xs text-slate-400">Accepted formats: JPG, PNG, PDF · Up to {MAX_FILES} files</p>
      </div>

      {attachments.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {attachments.map((item) => (
            <div key={item.id} className="relative rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <button
                type="button"
                onClick={() => removeAttachment(item.id)}
                className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-white shadow hover:bg-slate-700"
                aria-label="Remove file"
              >
                <X className="h-3.5 w-3.5" />
              </button>

              <div className="flex h-20 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                {item.isImage && item.previewUrl ? (
                  <img src={item.previewUrl} alt={item.fileName} className="h-full w-full object-cover" />
                ) : (
                  <FileText className="h-8 w-8 text-slate-400" />
                )}
              </div>

              <p className="mt-2 truncate text-xs font-medium text-slate-700">{item.fileName}</p>

              {item.uploading ? (
                <div className="mt-1 flex items-center gap-1 text-xs text-sky-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading...
                </div>
              ) : item.error ? (
                <div className="mt-1 flex items-center gap-1 text-xs text-red-500">
                  <AlertCircle className="h-3 w-3" />
                  {item.error}
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                  <CheckCircle2 className="h-3 w-3" />
                  Uploaded
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default AppealFileUpload
